import { useMemo, useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Skeleton } from '../../lib/shadcn/skeleton'
import { Badge } from '../../lib/shadcn/badge'
import { Button } from '../../lib/shadcn/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { cn } from '../../lib/shadcn/utils'
import { Sparkles, TrendingUp, TrendingDown, Minus, RefreshCw, AlertCircle, Package } from 'lucide-react'
import { HelpText } from '../../components/HelpText'

export type BagSize = 'Small' | 'Large' | 'Other'

export interface Prediction {
  product: string
  size: BagSize
  predicted_quantity: number
  baseline_avg: number
  trend: 'up' | 'down' | 'flat'
  confidence: 'low' | 'medium' | 'high'
  rationale: string
}

export interface SizeSummary {
  predicted_total: number
  baseline_total: number
}

export interface ForecastData {
  weekStart: string
  weekEnd: string
  generatedAt: string
  model: string | null
  predictions: Prediction[]
  sizeSummary?: Partial<Record<BagSize, SizeSummary>>
  note?: string
}

function formatDate(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const TREND_ICON = {
  up: TrendingUp,
  down: TrendingDown,
  flat: Minus,
}

const CONFIDENCE_VARIANT: Record<Prediction['confidence'], 'default' | 'secondary' | 'outline'> = {
  high: 'default',
  medium: 'secondary',
  low: 'outline',
}

const SIZE_BADGE_CLASS: Record<BagSize, string> = {
  Small: 'bg-chart-1/15 text-chart-1 border-chart-1/30',
  Large: 'bg-chart-2/15 text-chart-2 border-chart-2/30',
  Other: 'bg-muted text-muted-foreground',
}

const CHART_LIMIT = 10

type SizeFilter = 'all' | BagSize

interface ChartRow {
  product: string
  predicted?: number
  baseline?: number
  small?: number
  large?: number
  other?: number
}

export default function OrderForecastPanel({
  data,
  loading,
  refreshing,
  error,
  onRefresh,
}: {
  data: ForecastData | undefined
  loading: boolean
  refreshing: boolean
  error: string | null
  onRefresh: () => void
}) {
  const [sizeFilter, setSizeFilter] = useState<SizeFilter>('all')

  const predictions = data?.predictions ?? []

  const availableSizes = useMemo(() => {
    const sizes = new Set<BagSize>()
    for (const p of predictions) sizes.add(p.size)
    const ordered: BagSize[] = ['Small', 'Large', 'Other']
    return ordered.filter(s => sizes.has(s))
  }, [predictions])

  const filteredPredictions = useMemo(
    () => (sizeFilter === 'all' ? predictions : predictions.filter(p => p.size === sizeFilter)),
    [predictions, sizeFilter]
  )

  const chartData = useMemo<ChartRow[]>(() => {
    if (sizeFilter !== 'all') {
      return filteredPredictions.slice(0, CHART_LIMIT).map(p => ({
        product: p.product,
        predicted: p.predicted_quantity,
        baseline: p.baseline_avg,
      }))
    }
    // "All" view: pivot each product into small/large columns so the size
    // breakdown is visible directly in the chart.
    const byProduct = new Map<string, { product: string; small: number; large: number; other: number }>()
    for (const p of predictions) {
      const entry = byProduct.get(p.product) ?? { product: p.product, small: 0, large: 0, other: 0 }
      if (p.size === 'Small') entry.small += p.predicted_quantity
      else if (p.size === 'Large') entry.large += p.predicted_quantity
      else entry.other += p.predicted_quantity
      byProduct.set(p.product, entry)
    }
    return Array.from(byProduct.values())
      .sort((a, b) => b.small + b.large + b.other - (a.small + a.large + a.other))
      .slice(0, CHART_LIMIT)
  }, [predictions, filteredPredictions, sizeFilter])

  const sizeSummary = data?.sizeSummary

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Next week&apos;s order forecast
          </CardTitle>
          <CardDescription><HelpText>
            {data ? (
              <>
                AI-predicted units per product and bag size for {formatDate(data.weekStart)} –{' '}
                {formatDate(data.weekEnd)}, based on recent weekly order history.
              </>
            ) : (
              'AI-predicted units per product and bag size for the upcoming week, based on recent order history.'
            )}
          </HelpText></CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading || refreshing}>
          <RefreshCw className={cn('w-4 h-4 mr-2', refreshing && 'animate-spin')} />
          Regenerate
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}
        {data?.note && (
          <div className="flex items-center gap-2 p-3 rounded-md bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {data.note}
          </div>
        )}

        {loading ? (
          <>
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-[280px] w-full" />
            <Skeleton className="h-64 w-full" />
          </>
        ) : !data ? (
          <div className="py-16 flex flex-col items-center gap-3 text-center">
            <Sparkles className="w-8 h-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No forecast generated yet. Click Regenerate to create next week&apos;s forecast.
            </p>
            <Button size="sm" onClick={onRefresh} disabled={loading || refreshing}>
              <RefreshCw className={cn('w-4 h-4 mr-2', refreshing && 'animate-spin')} />
              Regenerate
            </Button>
          </div>
        ) : predictions.length === 0 ? (
          <p className="py-16 text-sm text-muted-foreground text-center">
            Not enough order history yet to generate a forecast.
          </p>
        ) : (
          <>
            {sizeSummary && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(['Small', 'Large'] as const).map(size => {
                  const summary = sizeSummary[size]
                  if (!summary) return null
                  const delta = summary.predicted_total - summary.baseline_total
                  return (
                    <div
                      key={size}
                      className="flex items-center gap-3 rounded-lg border border-border p-3"
                    >
                      <div
                        className={cn(
                          'flex items-center justify-center w-9 h-9 rounded-md shrink-0',
                          size === 'Small' ? 'bg-chart-1/15 text-chart-1' : 'bg-chart-2/15 text-chart-2'
                        )}
                      >
                        <Package className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">{size} bags forecast</p>
                        <p className="text-lg font-semibold tabular-nums leading-tight">
                          {summary.predicted_total}
                          <span className="text-xs font-normal text-muted-foreground ml-1">units</span>
                        </p>
                        <p
                          className={cn(
                            'text-xs tabular-nums',
                            delta > 0 && 'text-emerald-600 dark:text-emerald-400',
                            delta < 0 && 'text-red-600 dark:text-red-400',
                            delta === 0 && 'text-muted-foreground'
                          )}
                        >
                          {delta > 0 ? '+' : ''}
                          {Math.round(delta * 10) / 10} vs recent avg
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            <div className="flex items-center gap-1">
              <Button
                variant={sizeFilter === 'all' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2.5 text-xs"
                onClick={() => setSizeFilter('all')}
              >
                All sizes
              </Button>
              {availableSizes.map(size => (
                <Button
                  key={size}
                  variant={sizeFilter === size ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setSizeFilter(size)}
                >
                  {size}
                </Button>
              ))}
            </div>

            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 32 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="product"
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                  height={70}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 8,
                    color: 'hsl(var(--popover-foreground))',
                  }}
                  formatter={(value: unknown, name: unknown): [string, string] => {
                    const label =
                      name === 'baseline'
                        ? 'Recent 3-wk avg'
                        : name === 'small'
                          ? 'Small (predicted)'
                          : name === 'large'
                            ? 'Large (predicted)'
                            : name === 'other'
                              ? 'Other (predicted)'
                              : 'Predicted'
                    return [`${value} units`, label]
                  }}
                />
                <Legend
                  formatter={value =>
                    value === 'baseline'
                      ? 'Recent 3-wk avg'
                      : value === 'small'
                        ? 'Small'
                        : value === 'large'
                          ? 'Large'
                          : value === 'other'
                            ? 'Other'
                            : 'AI prediction'
                  }
                  wrapperStyle={{ fontSize: 12 }}
                />
                {sizeFilter === 'all' ? (
                  <>
                    <Bar dataKey="small" name="small" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="large" name="large" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                  </>
                ) : (
                  <>
                    <Bar dataKey="baseline" name="baseline" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="predicted" name="predicted" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                  </>
                )}
              </BarChart>
            </ResponsiveContainer>

            <div className="rounded-md border border-border overflow-auto max-h-80">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead className="text-right">Predicted</TableHead>
                    <TableHead className="text-right">Recent avg</TableHead>
                    <TableHead>Trend</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Why</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPredictions.map(p => {
                    const TrendIcon = TREND_ICON[p.trend]
                    return (
                      <TableRow key={`${p.product}-${p.size}`}>
                        <TableCell className="font-medium whitespace-nowrap">{p.product}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn('font-normal', SIZE_BADGE_CLASS[p.size])}>
                            {p.size}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">
                          {p.predicted_quantity}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {p.baseline_avg}
                        </TableCell>
                        <TableCell>
                          <TrendIcon
                            className={cn(
                              'w-4 h-4',
                              p.trend === 'up' && 'text-emerald-600 dark:text-emerald-400',
                              p.trend === 'down' && 'text-red-600 dark:text-red-400',
                              p.trend === 'flat' && 'text-muted-foreground'
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Badge variant={CONFIDENCE_VARIANT[p.confidence]} className="capitalize">
                            {p.confidence}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[280px]">
                          {p.rationale || '—'}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            {data && (
              <p className="text-xs text-muted-foreground">
                Generated {new Date(data.generatedAt).toLocaleString()}
                {data.model ? ` · ${data.model}` : ' · baseline average (AI unavailable)'}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
